import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { DISCLOSURE, money } from "./Give";

export default function GiveThankYou() {
  const [sp] = useSearchParams();
  const session_id = sp.get("session_id");
  const { user } = useApp();
  const [st, setSt] = useState({ state: "polling" });
  const attempts = useRef(0);

  useEffect(() => {
    if (!session_id) { setSt({ state: "error" }); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/giving/status/${session_id}`);
        if (["paid", "active", "trialing"].includes(data.status)) { setSt({ state: "done", gift: data }); return; }
        if (data.status === "expired") { setSt({ state: "failed" }); return; }
        if (attempts.current >= 12) { setSt({ state: "timeout" }); return; }
        timer = setTimeout(poll, 2000);
      } catch { setSt({ state: "error" }); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [session_id]);

  const g = st.gift;
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid={`give-thankyou-${st.state}`}>
      <div className="rounded-3xl border p-8 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        {st.state === "polling" && (<><Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: "var(--burgundy)" }} /><div className="eyebrow mb-2">Confirming your gift</div><p className="text-sm" style={{ color: "var(--text-muted)" }}>One moment while we confirm with Stripe.</p></>)}
        {st.state === "done" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--burgundy)" }} />
            <div className="eyebrow mb-2">Received with gratitude</div>
            <h1 className="font-serif text-3xl sm:text-4xl mb-3" style={{ color: "var(--burgundy)" }}>Thank you for supporting Temple of the Human Heart.</h1>
            <p className="text-sm mb-6" style={{ color: "var(--text-obsidian)" }} data-testid="give-thankyou-detail">
              {g.giving_type === "monthly"
                ? <>You selected <strong>monthly support of {money(g.amount_cents, g.currency)} per month</strong>.{g.current_period_end ? <> Your next billing date is {g.current_period_end.slice(0, 10)}.</> : null} You may cancel at any time; cancellation takes effect at the end of the current billing period.</>
                : <>Your <strong>one-time gift of {money(g.amount_cents, g.currency)}</strong> has been received.</>}
            </p>
            {g.giving_type === "monthly" && g.manage_token && (
              <div className="rounded-2xl border p-4 text-left text-sm mb-6" style={{ borderColor: "var(--gold)", background: "var(--bg-cream)" }} data-testid="give-manage-link-box">
                <div className="eyebrow mb-1">Manage your monthly support</div>
                <p className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>Save this private link. It lets you view or cancel your monthly support without an account.</p>
                <Link to={`/give/manage/${g.manage_token}`} className="ink-link break-all" style={{ color: "var(--burgundy)" }} data-testid="give-manage-link">{window.location.origin}/give/manage/{g.manage_token}</Link>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {user && <Link to="/my-temple" data-testid="give-thankyou-mytemple-btn" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Open My Temple <ArrowRight className="w-4 h-4" /></Link>}
              <Link to="/" data-testid="give-thankyou-home-btn" className="rounded-full px-6 py-3 text-sm font-medium border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>Return home</Link>
            </div>
            <p className="mt-6 text-xs" style={{ color: "var(--text-muted)" }}>{DISCLOSURE}</p>
          </>
        )}
        {["failed", "timeout", "error"].includes(st.state) && (
          <>
            <AlertTriangle className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--rose-dark)" }} />
            <h2 className="font-serif text-2xl mb-3" style={{ color: "var(--burgundy)" }}>{st.state === "failed" ? "This checkout was not completed." : "We couldn't confirm this yet."}</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>If your payment completed, it will be recorded once Stripe confirms it. You have not been charged twice.</p>
            <Link to="/give" className="rounded-full px-5 py-2.5 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Back to Give</Link>
          </>
        )}
      </div>
    </div>
  );
}
